import { create } from "zustand";

/**
 * Docked "Messaging" widget — UI-ONLY client state (a LinkedIn-style bottom-right
 * dock over the EXISTING DM system). This store holds NO server data: it never
 * caches a conversation, a message, or an unread count — those come from the DM
 * React-Query hooks (useConversations / useMessages / …) rendered VERBATIM. All
 * this store owns is the dock's presentation state: whether the conversation-list
 * panel is expanded, and which chat panes are open vs. minimized.
 *
 * A single global store (the app's `create()` convention for cross-tree UI state)
 * — mirroring `modules/booking/state/cartStore.tsx`'s Zustand usage, but a plain
 * singleton rather than a context-scoped store because the dock is mounted ONCE at
 * the app shell and `openConversation(id)` must be callable from anywhere (e.g. a
 * future "Message" affordance on a profile) without threading a provider.
 *
 * Cap: at most `MAX_OPEN_PANES` panes are simultaneously OPEN (non-minimized) —
 * opening or restoring beyond the cap auto-minimizes the OLDEST open pane(s) so the
 * dock never has too many active threads. Minimized panes stay in state and are
 * not closed; the renderer shows as many recent headers as the viewport can hold
 * after giving visible slots to open conversations.
 */

export interface DockPane {
  /** The DM conversation this pane shows. Identity is the conversation_id — a pane
   *  is never keyed on a user_id (which the UI must never leak). */
  conversationId: string;
  /** When true the pane is collapsed to just its header bar (still in the dock). */
  minimized: boolean;
}

export interface MessagingDockState {
  /** Account that owns the presentation state. A changed/null auth identity
   *  clears every pane before the dock can render for another account. */
  ownerId: string | null;
  /** Whether the conversation-LIST panel is expanded above the collapsed bar. This
   *  is independent of the open chat panes — collapsing the list never closes a
   *  pane, and opening a pane never expands the list. */
  expanded: boolean;
  /** Open + minimized chat panes, ordered OLDEST→NEWEST by last activation. */
  panes: DockPane[];
  /** Bind the singleton to the current authenticated account. */
  bindOwner: (ownerId: string | null) => void;
  /** Toggle the conversation-list panel open/closed. */
  toggleDock: () => void;
  /** Open a conversation's chat pane: add it (as the newest OPEN pane) if absent,
   *  or un-minimize + re-activate it if already present. Does NOT touch `expanded`
   *  (opening a pane is independent of the list panel). Enforces the open cap. */
  openConversation: (conversationId: string) => void;
  /** Fully remove a pane from the dock. */
  closePane: (conversationId: string) => void;
  /** Collapse a pane without closing it; its header appears when space permits. */
  minimizePane: (conversationId: string) => void;
  /** Restore a minimized pane to open + re-activate it (enforces the open cap). */
  restorePane: (conversationId: string) => void;
}

/**
 * The maximum number of simultaneously-OPEN (non-minimized) chat panes. Opening or
 * restoring a pane beyond this cap auto-minimizes the OLDEST open pane(s) so the
 * dock limits active threads. The renderer separately includes open and minimized
 * panes in its viewport-width budget. Minimized panes do not count against this
 * active-thread cap.
 */
export const MAX_OPEN_PANES = 3;

/**
 * Fold the OLDEST open panes to minimized until at most `MAX_OPEN_PANES` remain
 * open. "Oldest" = earliest in the array (panes are ordered oldest→newest by last
 * activation), so the most-recently-activated panes stay open.
 */
function enforceOpenCap(panes: DockPane[]): DockPane[] {
  const openCount = panes.reduce((n, p) => (p.minimized ? n : n + 1), 0);
  if (openCount <= MAX_OPEN_PANES) return panes;
  let toMinimize = openCount - MAX_OPEN_PANES;
  return panes.map((p) => {
    if (!p.minimized && toMinimize > 0) {
      toMinimize -= 1;
      return { ...p, minimized: true };
    }
    return p;
  });
}

/**
 * Re-add `conversationId` as the NEWEST open pane (moved to the array tail, so a
 * re-open/restore also brings it to the front-most, most-recent slot), then enforce
 * the open cap. Shared by `openConversation` (add-or-reactivate) + `restorePane`.
 */
function activate(panes: DockPane[], conversationId: string): DockPane[] {
  const without = panes.filter((p) => p.conversationId !== conversationId);
  return enforceOpenCap([...without, { conversationId, minimized: false }]);
}

export const useMessagingDock = create<MessagingDockState>((set) => ({
  ownerId: null,
  expanded: false,
  panes: [],
  bindOwner: (ownerId) =>
    set((state) =>
      state.ownerId === ownerId
        ? state
        : { ownerId, expanded: false, panes: [] },
    ),
  toggleDock: () => set((s) => ({ expanded: !s.expanded })),
  openConversation: (conversationId) =>
    set((s) => ({ panes: activate(s.panes, conversationId) })),
  closePane: (conversationId) =>
    set((s) => ({
      panes: s.panes.filter((p) => p.conversationId !== conversationId),
    })),
  minimizePane: (conversationId) =>
    set((s) => ({
      panes: s.panes.map((p) =>
        p.conversationId === conversationId ? { ...p, minimized: true } : p,
      ),
    })),
  restorePane: (conversationId) =>
    set((s) =>
      s.panes.some((p) => p.conversationId === conversationId)
        ? { panes: activate(s.panes, conversationId) }
        : s,
    ),
}));
